import { useMemo } from "react";
import { Search, X } from "lucide-react";
import type { PublicAnuncio } from "@/lib/public-anuncios";

export interface PersonagemFilterState {
  search: string;
  vocacao: string;
  anunciante: string;
  servidor: string;
  minPreco: string;
  maxPreco: string;
  minCoins: string;
  maxCoins: string;
  minLevel: string;
  maxLevel: string;
  minBoss: string;
  maxBoss: string;
}

export const defaultPersonagemFilters: PersonagemFilterState = {
  search: "",
  vocacao: "all",
  anunciante: "all",
  servidor: "all",
  minPreco: "",
  maxPreco: "",
  minCoins: "",
  maxCoins: "",
  minLevel: "",
  maxLevel: "",
  minBoss: "",
  maxBoss: "",
};

export const filterPersonagemAnuncios = (
  anuncios: PublicAnuncio[],
  f: PersonagemFilterState,
): PublicAnuncio[] => {
  const search = f.search.trim().toLowerCase();
  const minPreco = parseFloat(f.minPreco);
  const maxPreco = parseFloat(f.maxPreco);
  const minCoins = parseFloat(f.minCoins);
  const maxCoins = parseFloat(f.maxCoins);
  const minLevel = parseInt(f.minLevel, 10);
  const maxLevel = parseInt(f.maxLevel, 10);
  const minBoss = parseInt(f.minBoss, 10);
  const maxBoss = parseInt(f.maxBoss, 10);

  return anuncios.filter((a) => {
    const p = a.personagem;
    if (!p) return false;

    if (search) {
      const nome = (p.personagem_nome || a.item_nome || "").toLowerCase();
      if (!nome.includes(search)) return false;
    }
    if (f.vocacao !== "all" && (p.vocacao || "") !== f.vocacao) return false;
    if (f.anunciante !== "all" && (a.apelido || "") !== f.anunciante) return false;
    if (f.servidor !== "all" && (a.servidor || p.servidor || "") !== f.servidor) return false;

    const preco = a.preco || 0;
    if (!isNaN(minPreco) && preco < minPreco) return false;
    if (!isNaN(maxPreco) && preco > maxPreco) return false;

    const coins = a.preco_coins || 0;
    if (!isNaN(minCoins) && coins < minCoins) return false;
    if (!isNaN(maxCoins) && coins > maxCoins) return false;



    const level = p.level || 0;
    if (!isNaN(minLevel) && level < minLevel) return false;
    if (!isNaN(maxLevel) && level > maxLevel) return false;

    const boss = p.boss_points || 0;
    if (!isNaN(minBoss) && boss < minBoss) return false;
    if (!isNaN(maxBoss) && boss > maxBoss) return false;

    return true;
  });
};

interface PersonagemFiltersProps {
  anuncios: PublicAnuncio[];
  value: PersonagemFilterState;
  onChange: (value: PersonagemFilterState) => void;
  vertical?: boolean;
}

const PersonagemFilters = ({ anuncios, value, onChange, vertical = false }: PersonagemFiltersProps) => {
  const set = (patch: Partial<PersonagemFilterState>) => onChange({ ...value, ...patch });

  const { vocacoes, anunciantes, servidores } = useMemo(() => {
    const vSet = new Set<string>();
    const aSet = new Set<string>();
    const sSet = new Set<string>();
    for (const a of anuncios) {
      const p = a.personagem;
      if (p?.vocacao) vSet.add(p.vocacao);
      if (a.apelido) aSet.add(a.apelido);
      const serv = a.servidor || p?.servidor;
      if (serv) sSet.add(serv);
    }
    const sort = (s: Set<string>) => Array.from(s).sort((x, y) => x.localeCompare(y));
    return { vocacoes: sort(vSet), anunciantes: sort(aSet), servidores: sort(sSet) };
  }, [anuncios]);

  const hasActive =
    value.search.trim() !== "" ||
    value.vocacao !== "all" ||
    value.anunciante !== "all" ||
    value.servidor !== "all" ||
    value.minPreco !== "" ||
    value.maxPreco !== "" ||
    value.minCoins !== "" ||
    value.maxCoins !== "" ||
    value.minLevel !== "" ||
    value.maxLevel !== "" ||
    value.minBoss !== "" ||
    value.maxBoss !== "";

  const labelCls = "font-pixel text-[7px] text-muted-foreground mb-1 block";
  const selectCls = "tibia-input rounded-sm text-xs w-full py-1.5";
  const numCls = "tibia-input rounded-sm text-xs w-full py-1.5";
  const combosCls = vertical ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 sm:grid-cols-3 gap-2";
  const rangesCls = vertical ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 sm:grid-cols-3 gap-2";

  return (
    <div className={`${vertical ? "" : "mb-6"} p-3 tibia-border-inner rounded-sm bg-stone-dark/40 space-y-3`}>

      {/* Search by name */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={value.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder="Pesquisar por nome..."
          className="tibia-input rounded-sm text-xs w-full py-1.5 pl-7 pr-2"
        />
      </div>

      {/* Combos */}
      <div className={combosCls}>
        <div>
          <label className={labelCls}>Vocação</label>
          <select className={selectCls} value={value.vocacao} onChange={(e) => set({ vocacao: e.target.value })}>
            <option value="all">Todas</option>
            {vocacoes.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Anunciante</label>
          <select className={selectCls} value={value.anunciante} onChange={(e) => set({ anunciante: e.target.value })}>
            <option value="all">Todos</option>
            {anunciantes.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Servidor</label>
          <select className={selectCls} value={value.servidor} onChange={(e) => set({ servidor: e.target.value })}>
            <option value="all">Todos</option>
            {servidores.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Ranges */}
      <div className={rangesCls}>
        <div>
          <label className={labelCls}>Valor (R$)</label>
          <div className="flex items-center gap-1">
            <input type="number" min={0} inputMode="numeric" placeholder="Mín" className={numCls}
              value={value.minPreco} onChange={(e) => set({ minPreco: e.target.value })} />
            <span className="text-muted-foreground text-xs">-</span>
            <input type="number" min={0} inputMode="numeric" placeholder="Máx" className={numCls}
              value={value.maxPreco} onChange={(e) => set({ maxPreco: e.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Valor (Coins)</label>
          <div className="flex items-center gap-1">
            <input type="number" min={0} inputMode="numeric" placeholder="Mín" className={numCls}
              value={value.minCoins} onChange={(e) => set({ minCoins: e.target.value })} />
            <span className="text-muted-foreground text-xs">-</span>
            <input type="number" min={0} inputMode="numeric" placeholder="Máx" className={numCls}
              value={value.maxCoins} onChange={(e) => set({ maxCoins: e.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Nível</label>
          <div className="flex items-center gap-1">
            <input type="number" min={0} inputMode="numeric" placeholder="Mín" className={numCls}
              value={value.minLevel} onChange={(e) => set({ minLevel: e.target.value })} />
            <span className="text-muted-foreground text-xs">-</span>
            <input type="number" min={0} inputMode="numeric" placeholder="Máx" className={numCls}
              value={value.maxLevel} onChange={(e) => set({ maxLevel: e.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Boss Points</label>
          <div className="flex items-center gap-1">
            <input type="number" min={0} inputMode="numeric" placeholder="Mín" className={numCls}
              value={value.minBoss} onChange={(e) => set({ minBoss: e.target.value })} />
            <span className="text-muted-foreground text-xs">-</span>
            <input type="number" min={0} inputMode="numeric" placeholder="Máx" className={numCls}
              value={value.maxBoss} onChange={(e) => set({ maxBoss: e.target.value })} />
          </div>
        </div>
      </div>

      {hasActive && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onChange(defaultPersonagemFilters)}
            className="flex items-center gap-1 px-2 py-1 tibia-border rounded-sm font-pixel text-[7px] text-muted-foreground hover:text-primary transition-colors"
          >
            <X className="w-3 h-3" />
            Limpar filtros
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonagemFilters;
